import React, { useEffect } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, ShieldCheck } from 'lucide-react';
import { useSiteTheme } from '../hooks/useSiteTheme';
import { usePageScroll } from '../hooks/usePageScroll';
import { LEGAL } from '../lib/legal';
import { LEGAL_DOCS } from '../components/legal/legalContent';
import { SiteHeader } from '../components/billing/SiteHeader';
import { SiteFooter } from '../components/legal/SiteFooter';

const heading = { fontFamily: 'Outfit, Inter, sans-serif' };

/**
 * /terms and /privacy — the legal documents, with a sticky table of contents on wide screens and anchor links for
 * every section. Readable signed in or out (the header adapts).
 */
export default function Legal({ doc: docProp }) {
  const params = useParams();
  const location = useLocation();
  const key = docProp || params.doc;
  const doc = LEGAL_DOCS[key];
  const [theme, toggleTheme] = useSiteTheme();
  usePageScroll(); // index.css hides body overflow for the editor; this page scrolls like a normal web page

  useEffect(() => {
    if (!doc) return;
    document.title = `${doc.title} — ${LEGAL.brand}`;
    const id = location.hash.replace('#', '');
    if (id) document.getElementById(id)?.scrollIntoView({ block: 'start' });
    else window.scrollTo(0, 0);
    return () => { document.title = LEGAL.brand; };
  }, [doc, location.hash]);

  if (!doc) return <Navigate to="/" replace />;
  const other = key === 'terms' ? LEGAL_DOCS.privacy : LEGAL_DOCS.terms;
  const otherPath = key === 'terms' ? '/privacy' : '/terms';
  const Icon = key === 'terms' ? FileText : ShieldCheck;

  return (
    <main className="min-h-[100dvh] flex flex-col bg-background text-foreground" data-testid={`legal-page-${key}`} data-theme={theme}>
      <SiteHeader theme={theme} onToggleTheme={toggleTheme} testId="legal-header" />
      <section className="flex-1 w-full max-w-[1040px] mx-auto px-4 sm:px-6 pb-12 pt-2 sm:pt-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="legal-back"><ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />Back to {LEGAL.brand}</Link>
        <div className="mt-4 flex items-start gap-3">
          <span className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-5 w-5" aria-hidden="true" /></span>
          <div className="min-w-0">
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={heading} data-testid="legal-title">{doc.title}</h1>
            <p className="mt-1 text-xs text-muted-foreground" data-testid="legal-updated">Last updated {LEGAL.lastUpdated} · <Link to={otherPath} className="underline underline-offset-2 hover:text-foreground" data-testid="legal-other-doc">{other.title}</Link> · <Link to="/help" className="underline underline-offset-2 hover:text-foreground" data-testid="legal-help-link">Help Center</Link></p>
          </div>
        </div>
        <p className="mt-5 max-w-[720px] text-base leading-relaxed text-muted-foreground" data-testid="legal-intro">{doc.intro}</p>

        <div className="mt-8 grid gap-8 lg:grid-cols-[230px_minmax(0,1fr)]">
          <nav aria-label="Contents" className="lg:sticky lg:top-6 lg:self-start rounded-xl border border-border bg-card p-4" data-testid="legal-toc">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Contents</div>
            <ol className="mt-2 space-y-1.5 text-[13px] columns-2 lg:columns-1 gap-x-4">
              {doc.sections.map((s) => (
                <li key={s.id} className="break-inside-avoid"><a href={`#${s.id}`} className="block rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={`legal-toc-${s.id}`}>{s.title}</a></li>
              ))}
            </ol>
          </nav>
          <article className="wp-legal min-w-0 max-w-[720px]" data-testid="legal-body">
            {doc.sections.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-6 pb-8" data-testid={`legal-section-${s.id}`}>
                <h2 className="text-xl font-semibold tracking-tight mb-3" style={heading}>{s.title}</h2>
                {s.body}
              </section>
            ))}
          </article>
        </div>
      </section>
      <SiteFooter testId="legal-footer" />
    </main>
  );
}
